import { SlidersHorizontal } from "lucide-react";
import { ToolbarButton } from "@/routes/app/contacts/ToolbarButton";
import { Button } from "@/shared/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/components/ui/DropdownMenu";
import {
  EMPTY_CONTACTS_FILTERS,
  hasActiveFilters,
  type ContactsFilters,
  type PresenceFilter,
} from "@/shared/lib/contacts";
import { useTags } from "@/shared/lib/tags";

const PRESENCE_OPTIONS: { value: "any" | PresenceFilter; label: string }[] = [
  { value: "any", label: "Any" },
  { value: "yes", label: "Has" },
  { value: "no", label: "None" },
];

const PRESENCE_ROWS: {
  key: "hasEmail" | "hasPhone" | "hasAddress";
  label: string;
}[] = [
  { key: "hasEmail", label: "Email" },
  { key: "hasPhone", label: "Phone" },
  { key: "hasAddress", label: "Address" },
];

interface FilterMenuProps {
  value: ContactsFilters;
  onChange: (next: ContactsFilters) => void;
}

/**
 * Contacts-list filter popover. Each presence attribute (email/phone/address)
 * is a tri-state segmented control (Any / Has / None); the tag filter is a
 * single-select chip list sourced from {@link useTags}. Built from Button
 * primitives rather than nested Radix Selects, which would close the dropdown
 * when their portalled option list is clicked.
 */
export function FilterMenu({ value, onChange }: FilterMenuProps) {
  const active = hasActiveFilters(value);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ToolbarButton
          aria-label={active ? "Filter contacts (active)" : "Filter contacts"}
          className="relative"
        >
          <SlidersHorizontal aria-hidden className="h-4 w-4" />
          {active ? (
            <span
              aria-hidden
              className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-brand"
            />
          ) : null}
        </ToolbarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-3">
        <FilterPanel value={value} onChange={onChange} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The filter controls themselves, independent of the dropdown shell. Split out
 * so the control logic can be exercised in tests without driving Radix's portal
 * in jsdom.
 */
export function FilterPanel({ value, onChange }: FilterMenuProps) {
  const tags = useTags();
  const tagItems = tags.data?.items ?? [];
  const active = hasActiveFilters(value);

  return (
    <div className="flex flex-col gap-3">
      {PRESENCE_ROWS.map((row) => (
        <fieldset key={row.key} className="flex flex-col gap-2.5">
          <legend className="type-label-sm tracking-wider uppercase text-text-muted">
            {row.label}
          </legend>
          <div className="flex gap-1.5" role="group" aria-label={row.label}>
            {PRESENCE_OPTIONS.map((opt) => {
              const selected = value[row.key] === opt.value;
              return (
                <Button
                  key={opt.value}
                  type="button"
                  size="sm"
                  variant={selected ? "default" : "ghost"}
                  aria-pressed={selected}
                  aria-label={`${row.label} ${opt.label}`}
                  // Selected uses the brand fill for contrast, but the filter
                  // chip isn't an elevated CTA — drop the brand drop shadow.
                  className="flex-1 shadow-none"
                  onClick={() => onChange({ ...value, [row.key]: opt.value })}
                >
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </fieldset>
      ))}

      <div className="flex flex-col gap-2.5">
        <span className="type-label-sm tracking-wider uppercase text-text-muted">
          Tag
        </span>
        <div
          className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto"
          role="group"
          aria-label="Tag"
        >
          <Button
            type="button"
            size="sm"
            variant={value.tag === "" ? "default" : "ghost"}
            aria-pressed={value.tag === ""}
            className="shadow-none"
            onClick={() => onChange({ ...value, tag: "" })}
          >
            Any tag
          </Button>
          {tags.isLoading ? (
            <span className="type-body-sm self-center text-text-muted">
              Loading…
            </span>
          ) : tagItems.length === 0 ? (
            <span className="type-body-sm self-center text-text-muted">
              No tags yet
            </span>
          ) : (
            tagItems.map((t) => {
              const selected = value.tag === t.name;
              return (
                <Button
                  key={t.id}
                  type="button"
                  size="sm"
                  variant={selected ? "default" : "ghost"}
                  aria-pressed={selected}
                  className="shadow-none"
                  onClick={() =>
                    onChange({ ...value, tag: selected ? "" : t.name })
                  }
                >
                  {t.name}
                </Button>
              );
            })
          )}
        </div>
      </div>

      {active ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="self-start"
          onClick={() => onChange(EMPTY_CONTACTS_FILTERS)}
        >
          Clear filters
        </Button>
      ) : null}
    </div>
  );
}
